import React, { useContext, useEffect, useState, useRef } from 'react';
import { Box, Grid, Typography, Divider, useMediaQuery, Button } from '@mui/material';
import { Screen } from '../../components';
import { makeStyles } from '@mui/styles';
import { OrdersContext } from '../../lib/contexts';
import OrderDetails from './components/OrderDetails';
import MiniDeliveryDetails from './components/MiniDeliveryDetails';
import { useNavigate } from 'react-router-dom';
import { ThemeContext } from '../../lib/contexts/ThemeContext';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import OrderListPopUp from './components/OrderListPopUp';
import Drawer from '@mui/material/Drawer';
import CircularProgress from '@mui/material/CircularProgress';

const useStyles = makeStyles((theme) => ({
  screen: {
    width: '96%',
    margin: '10px auto auto',
  },
  sectionHeader: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  sectionWrapper: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    margin: theme.spacing(2, 0),
  },
  section: {
    flex: 1,
    marginRight: theme.spacing(2),
    '&:last-child': {
      marginRight: 0,
    },
  },
  divider: {
    borderRight: `1px solid ${theme.palette.divider}`,
    height: '100%',
    margin: '0 16px',
  },
  horizontalDivider: {
    margin: theme.spacing(2, 0),
  },
  headerContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
}));

const reportIssueButton = () => {
  return (
    <Button
      id="feedback-button"
      className="feedback-button"
      sx={{
        display: 'flex',
        width: '155px',
        height: 'var(--3, 36px)',
        padding: 'var(--0, 0px) var(--3, 13px)',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 'var(--3, 13px)',
        borderRadius: 'var(--3, 7px)',
        border: 'var(--1, 2px) solid var(--color12, #1C124D)',
      }}
    >
      <Typography
        sx={{
          color: 'var(--color12, #1C124D)',
          textAlign: 'center',
          leadingTrim: 'both',
          textEdge: 'cap',
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: 700,
          lineHeight: 'normal',
        }}
      >
        Report Issue
      </Typography>
    </Button>
  );
};

const OrdersDetailsScreen = () => {
  const classes = useStyles();
  const {
    state: { ordersData, mappedOrdersData, isLoading, dataFetched },
  } = useContext(OrdersContext);
  const {
    state: { darkMode },
  } = useContext(ThemeContext);
  const navigate = useNavigate();

  const [nextOrders, setNextOrders] = useState([]);
  const [futureOrders, setFutureOrders] = useState([]);
  const [historyOrders, setHistoryOrders] = useState([]);
  const isMobileView = useMediaQuery('(max-width:600px)');
  const [selectedOrder, setSelectedOrder] = useState('Next');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [orderStatus, setOrderStatus] = useState('');

  const orderDetailsRefs = useRef([]);
  const [paddingBottom, setPaddingBottom] = useState('650px');

  const miniDeliveryDetailsRefs = useRef([]);
  const [mobilePaddingBottom, setMobilePaddingBottom] = useState('650px');

  useEffect(() => {
    if (ordersData) {
      sortOrders(ordersData);
    }
  }, [ordersData]);

  useEffect(() => {
    if (orderDetailsRefs.current.length > 0) {
      const heights = orderDetailsRefs.current.map((ref) => (ref ? ref.clientHeight : 0));
      const maxHeight = Math.max(...heights);
      const calculatedPadding = Math.max(650 - maxHeight, 0); // Ensure padding is not negative
      setPaddingBottom(`${calculatedPadding}px`);
    }
  }, [mappedOrdersData]);

  useEffect(() => {
    if (isMobileView && miniDeliveryDetailsRefs.current.length > 0) {
      const heights = miniDeliveryDetailsRefs.current.map((ref) => (ref ? ref.clientHeight : 0));
      const maxHeight = Math.max(...heights);
      const calculatedPadding = Math.max(650 - maxHeight, 0); // Ensure padding is not negative
      setMobilePaddingBottom(`${calculatedPadding}px`);
    }
  }, [mappedOrdersData, isMobileView]);

  const sortOrders = (orders) => {
    if (!dataFetched) {
      setHistoryOrders([]);
      setNextOrders([]);
      setFutureOrders([]);
      return;
    }
    const history = [];
    const next = [];
    const future = [];

    orders.forEach((order) => {
      switch (order.status) {
        case 'DELIVERED':
          history.push(order);
          break;
        case 'ON THE WAY':
          next.push(order);
          break;
        case 'FUTURE':
          future.push(order);
          break;
        default:
          break;
      }
    });

    setHistoryOrders(history);
    setNextOrders(next);
    setFutureOrders(future);
  };

  const handleOrderClick = (order) => {
    setSelectedOrder(order);
  };

  const handleViewMenuClick = () => {
    navigate('/orders');
  };

  const handleDrawerOpen = (status) => {
    setOrderStatus(status);
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const getOrderDetailsBody = () => {
    // Reset refs array length
    orderDetailsRefs.current = [];
    miniDeliveryDetailsRefs.current = [];

    if (isMobileView) {
      return (
        <Box id="order-details-mobile">
          <Box
            id="order-details-mobile-header"
            sx={{
              display: 'flex',
              width: '320px',
              alignItems: 'center',
              gap: '36px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                padding: '4px 0px',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '10px',
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: 'var(--color12, #1C124D)',
                  fontSize: '24px',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  lineHeight: '125%', // 30px
                }}
              >
                Your Orders
              </Typography>
              {history.length > 0 && reportIssueButton()}
            </Box>
          </Box>
          <Box
            id="order-details-mobile-body"
            sx={{
              display: 'flex',
              width: '327px',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '24px',
            }}
          >
            <Box
              id="timeline-menu"
              sx={{
                display: 'flex',
                padding: '0px 4px',
                alignItems: 'flex-start',
                gap: '12px',
                alignSelf: 'stretch',
                borderBottom: '2px solid var(--color3, #F5F4F9)',
              }}
            >
              <Box
                id="history"
                sx={{
                  display: 'flex',
                  padding: '0px 4px var(--2-5, 10px) 4px',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 'var(--3-5, 16px)',
                  flex: '1 0 0',
                  borderBottom:
                    selectedOrder === 'History' ? '1px solid var(--secondary-mint-accessilbe, #1D7E6B)' : 'none',
                }}
              >
                <Typography
                  sx={{
                    color:
                      selectedOrder === 'History'
                        ? 'var(--secondary-mint-accessilbe, #1D7E6B)'
                        : 'var(--color12, #1C124D)',
                    leadingTrim: 'both',
                    textEdge: 'cap',
                    fontSize: '16px',
                    fontStyle: 'normal',
                    fontWeight: 700,
                    lineHeight: 'normal',
                  }}
                  onClick={() => handleOrderClick('History')}
                >
                  History
                </Typography>
                {/* <ArrowForwardIcon onClick={() => handleDrawerOpen('HISTORY')} /> */}
              </Box>
              <Box
                id="next"
                sx={{
                  display: 'flex',
                  padding: '0px 4px var(--2-5, 10px) 4px',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 'var(--3-5, 16px)',
                  flex: '1 0 0',
                  borderBottom:
                    selectedOrder === 'Next' ? '1px solid var(--secondary-mint-accessilbe, #1D7E6B)' : 'none',
                }}
              >
                <Typography
                  sx={{
                    color:
                      selectedOrder === 'Next'
                        ? 'var(--secondary-mint-accessilbe, #1D7E6B)'
                        : 'var(--color12, #1C124D)',
                    leadingTrim: 'both',
                    textEdge: 'cap',
                    fontSize: '16px',
                    fontStyle: 'normal',
                    fontWeight: 700,
                    lineHeight: 'normal',
                  }}
                  onClick={() => handleOrderClick('Next')}
                >
                  Upcoming
                </Typography>
                {/* <ArrowForwardIcon onClick={() => handleDrawerOpen('UPCOMING')} /> */}
              </Box>
              <Box
                id="future"
                sx={{
                  display: 'flex',
                  padding: '0px 4px var(--2-5, 10px) 4px',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 'var(--3-5, 16px)',
                  flex: '1 0 0',
                  borderBottom:
                    selectedOrder === 'Future' ? '1px solid var(--secondary-mint-accessilbe, #1D7E6B)' : 'none',
                }}
              >
                <Typography
                  sx={{
                    color:
                      selectedOrder === 'Future'
                        ? 'var(--secondary-mint-accessilbe, #1D7E6B)'
                        : 'var(--color12, #1C124D)',
                    leadingTrim: 'both',
                    textEdge: 'cap',
                    fontSize: '16px',
                    fontStyle: 'normal',
                    fontWeight: 700,
                    lineHeight: 'normal',
                  }}
                  onClick={() => handleOrderClick('Future')}
                >
                  Future
                </Typography>
                {/* <ArrowForwardIcon onClick={() => handleDrawerOpen('FUTURE')} /> */}
              </Box>
            </Box>
            {isLoading ? (
              <CircularProgress />
            ) : (
              <>
                {selectedOrder === 'Next' &&
                  mappedOrdersData.upcoming.map((order, index) => (
                    <Box
                      key={order.id}
                      ref={(el) =>
                        index === 0
                          ? (orderDetailsRefs.current[index] = el)
                          : (miniDeliveryDetailsRefs.current[index] = el)
                      }
                    >
                      {index === 0 ? (
                        <OrderDetails
                          order={order}
                          showPopUps={false}
                          from="/orders/details"
                        />
                      ) : (
                        <MiniDeliveryDetails order={order} />
                      )}
                    </Box>
                  ))}
                {selectedOrder === 'Future' &&
                  mappedOrdersData.future.map((order, index) => (
                    <Box
                      key={order.id}
                      ref={(el) =>
                        index === 0
                          ? (orderDetailsRefs.current[index] = el)
                          : (miniDeliveryDetailsRefs.current[index] = el)
                      }
                    >
                      {index === 0 ? (
                        <OrderDetails
                          order={order}
                          showPopUps={false}
                          from="/orders/details"
                        />
                      ) : (
                        <MiniDeliveryDetails order={order} />
                      )}
                    </Box>
                  ))}
                {selectedOrder === 'History' &&
                  mappedOrdersData.history.map((order, index) => (
                    <Box
                      key={order.id}
                      ref={(el) =>
                        index === 0
                          ? (orderDetailsRefs.current[index] = el)
                          : (miniDeliveryDetailsRefs.current[index] = el)
                      }
                    >
                      {index === 0 ? (
                        <OrderDetails
                          order={order}
                          showPopUps={false}
                          from="/orders/details"
                        />
                      ) : (
                        <MiniDeliveryDetails order={order} />
                      )}
                    </Box>
                  ))}
              </>
            )}
          </Box>
        </Box>
      );
    } else {
      return (
        <Box>
          <Box
            id="order-details-header"
            sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}
          >
            <Typography
              variant="h4"
              sx={{ marginBottom: '20px' }}
            >
              Your Orders
            </Typography>
            {history.length > 0 && reportIssueButton()}
          </Box>
          <Grid
            container
            spacing={10}
          >
            {/* Column 3: History */}
            <Grid
              item
              xs={12}
              md={4}
              sx={{ minWidth: '400px' }}
            >
              <Box className={classes.section}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                  }}
                  onClick={() => handleDrawerOpen('HISTORY')}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      leadingTrim: 'both',
                      textEdge: 'cap',
                      fontSize: '20px',
                      fontStyle: 'normal',
                      fontWeight: 700,
                      lineHeight: 'normal',
                    }}
                  >
                    History
                  </Typography>
                  <ArrowForwardIcon
                    sx={{
                      color: '#6B7280',
                      cursor: 'pointer',
                      ml: 1,
                    }}
                  />
                </Box>
                <Divider
                  orientation="horizontal"
                  flexItem
                  className={classes.horizontalDivider}
                />
                {isLoading ? (
                  <CircularProgress />
                ) : mappedOrdersData && mappedOrdersData.history.length > 0 ? (
                  mappedOrdersData.history.slice(0, 1).map((order) => (
                    <Box key={order.id}>
                      <OrderDetails
                        order={order}
                        showPopUps={false}
                        from="/orders/details"
                        ref={(el) => (orderDetailsRefs.current[3] = el)}
                      />
                    </Box>
                  ))
                ) : (
                  <div
                    style={{
                      fontSize: '16px',
                      fontWeight: 400,
                      lineHeight: '125%',
                      color: '#1C124D',
                    }}
                  >
                    No orders in history
                  </div>
                )}
              </Box>
            </Grid>
            {/* Column 1: Next */}
            <Grid
              id="upcoming-orders"
              item
              xs={12}
              md={4}
              sx={{
                minWidth: '400px',
                paddingBottom: paddingBottom,
              }}
            >
              <Box className={classes.section}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                  }}
                  onClick={() => handleDrawerOpen('UPCOMING')}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      leadingTrim: 'both',
                      textEdge: 'cap',
                      fontSize: '20px',
                      fontStyle: 'normal',
                      fontWeight: 700,
                      lineHeight: 'normal',
                    }}
                  >
                    Upcoming
                  </Typography>
                  <ArrowForwardIcon
                    sx={{
                      color: '#6B7280',
                      cursor: 'pointer',
                      ml: 1,
                    }}
                  />
                </Box>
                <Divider
                  orientation="horizontal"
                  flexItem
                  className={classes.horizontalDivider}
                />
                {isLoading ? (
                  <CircularProgress />
                ) : mappedOrdersData && mappedOrdersData.upcoming.length > 0 ? (
                  mappedOrdersData.upcoming.slice(0, 1).map((order) => (
                    <Box key={order.id}>
                      <OrderDetails
                        order={order}
                        showPopUps={false}
                        from="/orders/details"
                        ref={(el) => (orderDetailsRefs.current[4] = el)}
                      />
                    </Box>
                  ))
                ) : (
                  <div
                    style={{
                      fontSize: '16px',
                      fontWeight: 400,
                      lineHeight: '125%',
                      color: '#1C124D',
                    }}
                  >
                    No upcoming orders
                  </div>
                )}
              </Box>
            </Grid>
            {/* Column 2: Future */}
            <Grid
              item
              xs={12}
              md={4}
              sx={{ minWidth: '400px' }}
            >
              <Box className={classes.section}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                  }}
                  onClick={() => handleDrawerOpen('FUTURE')}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      leadingTrim: 'both',
                      textEdge: 'cap',
                      fontSize: '20px',
                      fontStyle: 'normal',
                      fontWeight: 700,
                      lineHeight: 'normal',
                    }}
                  >
                    Future
                  </Typography>
                  <ArrowForwardIcon sx={{ color: '#6B7280', cursor: 'pointer', ml: 1 }} />
                </Box>
                <Divider
                  orientation="horizontal"
                  flexItem
                  className={classes.horizontalDivider}
                />
                {isLoading ? (
                  <CircularProgress />
                ) : mappedOrdersData && mappedOrdersData.future.length > 0 ? (
                  mappedOrdersData.future.slice(0, 1).map((order) => (
                    <Box key={order.id}>
                      <OrderDetails
                        order={order}
                        showPopUps={false}
                        from="/orders/details"
                        ref={(el) => (orderDetailsRefs.current[5] = el)}
                      />
                    </Box>
                  ))
                ) : (
                  <div
                    style={{
                      fontSize: '16px',
                      fontWeight: 400,
                      lineHeight: '125%',
                      color: '#1C124D',
                    }}
                  >
                    No orders in future yet
                  </div>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
      );
    }
  };

  const handleNavigateToOrderDetails = (orderId) => {
    navigate(`/orders/${orderId}`, { state: { from: '/orders/details' } });
  };

  return (
    <Screen
      title={'Your Orders'}
      showSidebarNav={true}
    >
      <Box
        id="order-details-body"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          padding: '20px',
          paddingBottom: isMobileView ? mobilePaddingBottom : paddingBottom,
        }}
      >
        {getOrderDetailsBody()}
      </Box>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleDrawerClose}
        PaperProps={{
          sx: {
            width: '440px',
            // borderRadius: 2,
            p: 2,
            backgroundColor: darkMode ? '#333' : '#fff',
            color: darkMode ? '#fff' : '#000',
          },
        }}
      >
        <OrderListPopUp
          status={orderStatus}
          onClose={handleDrawerClose}
        />
      </Drawer>
    </Screen>
  );
};

export default OrdersDetailsScreen;
